import React, { useEffect, useRef } from 'react';
import { useScrollLock } from '../../../hooks/useScrollLock';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASS: Record<ModalSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-5xl',
  full: 'sm:max-w-[min(96vw,1280px)]',
};

interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  /** Disable backdrop / Esc dismissal while async work is in flight. */
  busy?: boolean;
  size?: ModalSize;
  /** Compact "subtitle" line under the title. */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional content rendered to the right of the title (e.g. status pills). */
  headerEnd?: React.ReactNode;
  /** Optional sticky tab bar between header and body. */
  tabs?: React.ReactNode;
  /** Sticky footer slot for action buttons. */
  footer?: React.ReactNode;
  children: React.ReactNode;
  /** Set true for content panes that supply their own padding (e.g. an iframe). */
  contentFullBleed?: boolean;
  ariaLabel?: string;
}

/**
 * Modern, big-tech-inspired modal shell — bottom sheet on mobile,
 * centered card on tablet+. Designed as the single primitive every
 * admin "create / view" surface in this app should compose against
 * so the look + feel stays consistent without each modal re-inventing
 * spacing, scroll containment, focus management, or dismiss semantics.
 *
 * <p><b>Layout contract.</b>
 * <ul>
 *   <li><b>Mobile</b>: pinned to the bottom of the viewport with a
 *       rounded top edge (the thumb-zone). The drag handle hints at
 *       sheet-ness without us actually implementing drag (which would
 *       add a 12KB gesture dependency for marginal value). Inner
 *       content scrolls; the header + footer stay locked.</li>
 *   <li><b>Tablet+</b>: centered card with a fade-in. Outer padding
 *       is generous so the modal never feels cramped, even for
 *       multi-column forms. {@code size="xl"} reserves enough width
 *       to put an HTML email preview side-by-side with the form.</li>
 *   <li><b>Backdrop blur</b>: a 2px {@code backdrop-blur} sells the
 *       "focused dialog" intent on desktop without sacrificing perf
 *       on mobile (Safari handles it natively).</li>
 * </ul>
 *
 * <p><b>Behavior contract.</b> Esc + backdrop click dismiss when not
 * {@code busy}. Body scroll is locked while open. Focus is captured
 * inside the dialog (lightweight: we don't trap-and-cycle, but the
 * first focusable element gets initial focus, and the previously-
 * focused element is restored on close so keyboard users aren't
 * stranded at {@code <body>}).
 */
export const ModalShell: React.FC<ModalShellProps> = ({
  open,
  onClose,
  busy = false,
  size = 'md',
  eyebrow,
  title,
  description,
  headerEnd,
  tabs,
  footer,
  children,
  contentFullBleed,
  ariaLabel,
}) => {
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Lock background scroll for the lifetime of the open modal.
  // Two distinct admins reported "tap-and-scroll the modal also scrolls the
  // page underneath" on iOS — overflow:hidden on body does NOT stop that on
  // iOS Safari, so we use the shared iOS-safe (position:fixed) ref-counted lock.
  useScrollLock(open);

  // Focus management — capture, focus the first focusable element,
  // restore on unmount, and keep Tab inside the modal while it is open.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => {
      const first = cardRef.current?.querySelector<HTMLElement>(
        'input, textarea, select, button, [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    }, 30);
    return () => {
      window.clearTimeout(t);
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose, busy]);

  const trapFocus = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      cardRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) ?? [],
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel ?? (typeof title === 'string' ? title : undefined)}
    >
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity duration-150"
        onMouseDown={() => {
          if (!busy) onClose();
        }}
      />
      <div
        ref={cardRef}
        className={`relative w-full ${SIZE_CLASS[size]}
          max-h-[94vh] sm:max-h-[92vh]
          bg-white text-gray-900
          rounded-t-2xl sm:rounded-2xl sm:m-4
          shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)]
          ring-1 ring-black/5
          flex flex-col overflow-hidden
          motion-safe:animate-[modalIn_180ms_ease-out]`}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={trapFocus}
      >
        {/* Sheet drag handle (mobile only). Cosmetic but it makes the
            sheet feel like every other native iOS / Android sheet. */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="h-1 w-10 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-start gap-3 px-5 sm:px-6 pt-3 sm:pt-5 pb-3 sm:pb-4 border-b border-gray-100">
          <div className="min-w-0 flex-1">
            {eyebrow && (
              <div className="text-[11px] uppercase tracking-[0.08em] text-gray-500 font-semibold mb-1.5">
                {eyebrow}
              </div>
            )}
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 leading-snug break-words">
              {title}
            </h3>
            {description && (
              <div className="text-sm text-gray-500 mt-1 leading-snug">{description}</div>
            )}
          </div>
          {headerEnd && <div className="shrink-0 hidden sm:block">{headerEnd}</div>}
          <button
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="Close"
            className="shrink-0 -mr-1 -mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full
                       text-gray-400 hover:text-gray-700 hover:bg-gray-100 active:bg-gray-200
                       transition-colors disabled:opacity-40"
            disabled={busy}
          >
            <svg viewBox="0 0 20 20" fill="none" className="w-4 h-4">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {tabs && (
          <div className="px-3 sm:px-4 border-b border-gray-100 bg-gray-50/40">{tabs}</div>
        )}

        {/* Body */}
        <div
          className={`flex-1 overflow-y-auto overscroll-contain ${
            contentFullBleed ? '' : 'px-5 sm:px-6 py-4 sm:py-5'
          }`}
        >
          {children}
        </div>

        {footer && (
          <div className="px-5 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-white/95 backdrop-blur sticky bottom-0">
            {footer}
          </div>
        )}
      </div>

      {/* Local @keyframes — scoped at the document level via a global
          style block so a parent route doesn't have to add it to
          tailwind config. Cheap, idempotent. */}
      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: translateY(8px) scale(0.985); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (max-width: 640px) {
          @keyframes modalIn {
            from { opacity: 0; transform: translateY(24px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        }
      `}</style>
    </div>
  );
};

export default ModalShell;
